import React from "react";
import Head from "next/head";
import { useTranslations } from "next-intl";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import { useInView } from "@hooks/useInView";
import styles from "./StodumatPage.module.scss";

const STEPS = [
    {
        num: 1,
        titleKey: "box1Title",
        descKey: "box1Copy",
        icon: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        ),
    },
    {
        num: 2,
        titleKey: "box2Title",
        descKey: "box2Copy",
        icon: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
            </svg>
        ),
    },
    {
        num: 3,
        titleKey: "box3Title",
        descKey: "box3Copy",
        icon: (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
            </svg>
        ),
    },
];

export default function StodumatPage() {
    const t = useTranslations("StodumatPage");

    const [heroRef, heroInView] = useInView();
    const [stepsRef, stepsInView] = useInView();
    const [ctaSectionRef, ctaSectionInView] = useInView();
    const [bottomCtaRef, bottomCtaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">{"Stöðumat - " + WEBPAGE_TITLE}</title>
                <meta name="keywords" content="Stöðumat sveitarfélög" />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Stöðumat`}
                    key="og:title"
                />
                <meta
                    name="og:description"
                    content={t("desc")}
                    key="og:description"
                />
                <meta name="description" content={t("desc")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("badge")}</span>
                        <h1 className={styles.heroTitle}>{t("header")}</h1>
                        <p className={styles.heroDesc}>{t("desc")}</p>
                    </div>
                </section>

                <section
                    ref={stepsRef}
                    className={`${styles.steps} ${styles.revealTarget} ${stepsInView ? styles.revealed : ""}`}
                >
                    <div className={styles.stepsGrid}>
                        {STEPS.map((step) => (
                            <div key={step.num} className={styles.stepCard}>
                                <div className={styles.stepLeft}>
                                    <div className={styles.stepNumber}>
                                        {step.num}
                                    </div>
                                    <div className={styles.stepIcon}>
                                        {step.icon}
                                    </div>
                                </div>
                                <div className={styles.stepRight}>
                                    <h3 className={styles.stepTitle}>
                                        {t(step.titleKey)}
                                    </h3>
                                    <p className={styles.stepDesc}>
                                        {t(step.descKey)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section
                    ref={ctaSectionRef}
                    className={`${styles.ctaSection} ${styles.revealTarget} ${ctaSectionInView ? styles.revealed : ""}`}
                >
                    <div className={styles.ctaCard}>
                        <img
                            className={styles.ctaImage}
                            src="/img/sounargreining_img.png"
                            alt={t("sounargreiningButton")}
                        />
                        <div className={styles.ctaContent}>
                            <p className={styles.ctaText}>
                                {t("sounargreiningCopy")}
                            </p>
                            <a className={styles.ctaBtn} href="/hafa-samband">
                                {t("sounargreiningButton")}
                            </a>
                        </div>
                    </div>
                </section>

                <section
                    ref={bottomCtaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${bottomCtaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("bottomCtaTitle")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("bottomCtaDesc")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("bottomCtaButton")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
